/**
 * Acting on behalf of a client.
 *
 * Goals belong to a user, and most goal endpoints take no `userId` — they
 * resolve the caller from the token. The backend's escape hatch is the
 * `X-Target-User-Id` header: `get_user_id_from_context` honours it for callers
 * that are an AGENT, EMPLOYEE, ADMIN or super-user, and ignores it for everyone
 * else (see app/graphql_endpoints/utils/helper.py). That is how an advisor
 * creates and reads a *client's* goals rather than their own.
 *
 * Pass the result as an operation's `context` — Apollo's auth link spreads
 * incoming headers, so the token still goes along.
 */
export const asClient = (userId) =>
  userId == null || userId === ''
    ? {}
    : { headers: { 'X-Target-User-Id': String(userId) } };

/**
 * Goal ⇄ portfolio linkage, the way app_admin resolves it.
 *
 * A portfolio carries `goalId` / `goalData.goalId`, which is the authoritative
 * pointer back to a goal. Goals point the other way through their associated
 * accounts, their policy portfolio, or `associatedPortfolioId`. Matching on
 * either side tells us which goals a given set of portfolios owns.
 */
export function goalsOwnedBy(goals, portfolios) {
  const ownedGoalIds = new Set(
    (portfolios ?? [])
      .flatMap((p) => [p.goalId, p.goalData?.goalId])
      .filter((v) => v != null)
      .map(String),
  );
  const ownedKeys = new Set(
    (portfolios ?? [])
      .flatMap((p) => [p.securitySymbol, p.portfolioName])
      .filter(Boolean)
      .map(String),
  );

  const keysOf = (g) =>
    [
      ...(g.accountData?.associatedAccounts ?? []).map((a) => a?.securitySymbol),
      g.policyPortfolio?.securitySymbol,
      g.associatedPortfolioId,
    ]
      .filter(Boolean)
      .map(String);

  return (goals ?? []).filter(
    (g) => ownedGoalIds.has(String(g.goalId)) || keysOf(g).some((k) => ownedKeys.has(k)),
  );
}
